import { Trans, useLingui } from "@lingui/react/macro";
import type { NavLink } from "@rakazo/contracts";
import { ANDROID_APK_URL, IOS_APP_STORE_URL } from "@rakazo/contracts";
import {
  AppMarkStack,
  Button,
  buttonVariants,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@rakazo/ui-web";
import {
  Download,
  FolderOpen,
  Gauge,
  LayoutGrid,
  Link2,
  LogOut,
  Plug,
  ScrollText,
  Settings,
  ShieldCheck,
  Smartphone,
  SquareKanban,
  UserPlus,
  UserRound,
} from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { useSidebarUpdate } from "./DesktopUpdates";
import { IconTooltip } from "./IconTooltip";

type Props = {
  mini?: boolean;
  /** "top" sits at the end of the app's top bar: apps and settings icons, then an account pill. */
  placement?: "sidebar" | "top";
  name: string;
  initials: string | null;
  image?: string | null;
  logos?: Array<{ name: string; logo?: string | null }>;
  email?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onIntegrations: () => void;
  onSkills: () => void;
  onBoard?: () => void;
  links?: NavLink[];
  onLink?: (link: NavLink) => void;
  onArtifacts: () => void;
  onSettings: () => void;
  onUsage: () => void;
  onInvite?: () => void;
  /** Present only for the deployment's admins. */
  onAdmin?: () => void;
  onLogOut: () => void;
  signingOut: boolean;
  logOutError: string | null;
};

// Marks are 24px wide, overlap by 2px, and need 2px for the outer ring.
export function sidebarMarkCount(availableWidth: number) {
  return Math.max(0, Math.min(3, Math.floor((availableWidth - 26) / 22) + 1));
}

export function SidebarFooter({
  mini = false,
  placement = "sidebar",
  name,
  initials,
  image,
  logos = [],
  email,
  open,
  onOpenChange,
  onIntegrations,
  onSkills,
  onBoard,
  links = [],
  onLink,
  onArtifacts,
  onSettings,
  onUsage,
  onInvite,
  onAdmin,
  onLogOut,
  signingOut,
  logOutError,
}: Props) {
  const { t } = useLingui();
  const mobileAppUrl = /Android/i.test(navigator.userAgent) ? ANDROID_APK_URL : IOS_APP_STORE_URL;
  const update = useSidebarUpdate();
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [markCount, setMarkCount] = useState(3);
  useLayoutEffect(() => {
    const pill = pillRef.current;
    const label = labelRef.current;
    if (!pill || !label || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const style = getComputedStyle(pill);
      const available =
        pill.clientWidth -
        Number.parseFloat(style.paddingLeft) -
        Number.parseFloat(style.paddingRight) -
        label.scrollWidth -
        Number.parseFloat(style.columnGap);
      setMarkCount(sidebarMarkCount(available));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(pill);
    observer.observe(label);
    measure();
    return () => observer.disconnect();
  }, [mini]);
  const top = placement === "top";
  const avatar =
    image && failedImage !== image ? (
      <img
        src={image}
        alt=""
        className="size-full object-cover"
        onError={() => setFailedImage(image)}
      />
    ) : initials ? (
      initials
    ) : (
      <UserRound size={18} aria-hidden="true" />
    );
  return (
    <div
      data-testid="sidebar-footer"
      className={`flex shrink-0 items-center bg-transparent ${top ? "gap-1.5" : mini ? "flex-col gap-2 px-1 py-2" : "gap-2 px-2.5 py-3"}`}
    >
      {top ? (
        <>
          <IconTooltip label={t`Connect apps`}>
            <Button
              variant="ghost"
              size="icon-lg"
              aria-label={t`Connect apps`}
              onClick={onIntegrations}
              className="rounded-full text-muted-foreground"
            >
              <LayoutGrid strokeWidth={1.75} aria-hidden="true" />
            </Button>
          </IconTooltip>
          <IconTooltip label={t`Settings`}>
            <Button
              variant="ghost"
              size="icon-lg"
              aria-label={t`Settings`}
              onClick={onSettings}
              className="rounded-full text-muted-foreground"
            >
              <Settings strokeWidth={1.75} aria-hidden="true" />
            </Button>
          </IconTooltip>
        </>
      ) : null}
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger
          data-testid="user-menu-trigger"
          aria-label={t`Account menu for ${name}`}
          className={
            top
              ? "flex h-10 max-w-48 shrink-0 items-center gap-2 rounded-full border border-border bg-sidebar ps-1 pe-3.5 text-sm font-medium text-foreground shadow-panel outline-none hover:border-foreground/30 focus-visible:ring-2 focus-visible:ring-ring"
              : "grid size-9 shrink-0 place-items-center overflow-hidden rounded-full border border-border bg-sidebar-accent text-sm font-medium text-foreground outline-none hover:border-foreground/30 focus-visible:ring-2 focus-visible:ring-ring"
          }
        >
          {top ? (
            <>
              <span className="grid size-8 shrink-0 place-items-center overflow-hidden rounded-full bg-primary text-[13px] text-primary-foreground">
                {avatar}
              </span>
              <span className="min-w-0 truncate">{name}</span>
            </>
          ) : (
            avatar
          )}
        </PopoverTrigger>
        {open ? (
          <PopoverContent
            side={top ? "bottom" : "top"}
            sideOffset={8}
            align={top ? "end" : "start"}
            className="max-h-(--available-height) w-[295px] max-w-[calc(100vw-69px)] gap-0 overflow-y-auto p-1 *:shrink-0 data-closed:animate-none"
          >
            <div
              data-testid="account-menu-header"
              className="mb-1 min-w-0 border-b border-border px-2.5 py-2.5"
            >
              <p className="truncate text-sm font-medium">{name}</p>
              {email && email !== name ? (
                <p className="truncate text-xs text-muted-foreground">{email}</p>
              ) : null}
            </div>
            {mini ? (
              <Button
                variant="ghost"
                className="h-9 w-full justify-start gap-2.5 font-normal"
                onClick={onIntegrations}
              >
                <Plug className="text-muted-foreground" strokeWidth={1.75} />
                <Trans>Connect apps</Trans>
              </Button>
            ) : null}
            {/* The top bar keeps Settings one click away beside the menu. */}
            {top ? null : (
              <Button
                variant="ghost"
                className="h-9 w-full justify-start gap-2.5 font-normal"
                aria-label={t`Settings`}
                onClick={onSettings}
              >
                <Settings className="text-muted-foreground" strokeWidth={1.75} />
                <Trans>Settings</Trans>
              </Button>
            )}
            <Button
              variant="ghost"
              className="h-9 w-full justify-start gap-2.5 font-normal"
              onClick={onSkills}
            >
              <ScrollText className="text-muted-foreground" strokeWidth={1.75} />
              <Trans>Skills</Trans>
            </Button>
            <Button
              variant="ghost"
              className="h-9 w-full justify-start gap-2.5 font-normal"
              onClick={onArtifacts}
            >
              <FolderOpen className="text-muted-foreground" strokeWidth={1.75} />
              <Trans>Artifacts</Trans>
            </Button>
            {onBoard ? (
              <Button
                variant="ghost"
                className="h-9 w-full justify-start gap-2.5 font-normal"
                onClick={onBoard}
              >
                <SquareKanban className="text-muted-foreground" strokeWidth={1.75} />
                <Trans>Board</Trans>
              </Button>
            ) : null}
            {links.map((link) => (
              <Button
                key={link.id}
                variant="ghost"
                className="h-9 w-full justify-start gap-2.5 font-normal"
                onClick={() => onLink?.(link)}
              >
                <Link2 className="text-muted-foreground" strokeWidth={1.75} />
                {link.label}
              </Button>
            ))}
            <Button
              variant="ghost"
              className="h-9 w-full justify-start gap-2.5 font-normal"
              onClick={onUsage}
            >
              <Gauge className="text-muted-foreground" strokeWidth={1.75} />
              <Trans>Usage</Trans>
            </Button>
            {onAdmin ? (
              <Button
                variant="ghost"
                className="h-9 w-full justify-start gap-2.5 font-normal"
                onClick={onAdmin}
              >
                <ShieldCheck className="text-muted-foreground" strokeWidth={1.75} />
                <Trans>Admin</Trans>
              </Button>
            ) : null}
            <div className="my-1 border-t border-border" />
            {mobileAppUrl ? (
              <a
                className={`${buttonVariants({ variant: "ghost" })} h-9 w-full justify-start gap-2.5 font-normal`}
                href={mobileAppUrl}
                target="_blank"
                rel="noreferrer"
              >
                <Smartphone className="text-muted-foreground" strokeWidth={1.75} />
                <Trans>Get the mobile app</Trans>
              </a>
            ) : null}
            {onInvite ? (
              <Button
                variant="ghost"
                className="h-9 w-full justify-start gap-2.5 font-normal"
                onClick={onInvite}
              >
                <UserPlus className="text-muted-foreground" strokeWidth={1.75} />
                <Trans>Invite friends</Trans>
              </Button>
            ) : null}
            <div className="my-1 border-t border-border" />
            <Button
              variant="ghost"
              className="h-9 w-full justify-start gap-2.5 font-normal"
              disabled={signingOut}
              onClick={onLogOut}
            >
              <LogOut className="text-muted-foreground" strokeWidth={1.75} />
              <Trans>Log out</Trans>
            </Button>
            {logOutError ? (
              <p role="alert" className="px-2.5 pt-1 pb-2 text-[13px] text-destructive">
                {logOutError}
              </p>
            ) : null}
          </PopoverContent>
        ) : null}
      </Popover>
      {!mini && !top ? (
        <Button
          variant="outline"
          ref={pillRef}
          onClick={onIntegrations}
          className="h-9 min-w-0 flex-1 justify-between bg-sidebar-accent hover:border-foreground/30 hover:bg-sidebar-accent dark:hover:bg-sidebar-accent"
        >
          <span ref={labelRef} className="min-w-0 truncate">
            <Trans>Connect apps</Trans>
          </span>
          <AppMarkStack marks={logos.slice(0, markCount)} ringClassName="ring-sidebar-accent" />
        </Button>
      ) : null}
      {update.available ? (
        <IconTooltip label={t`Install update`}>
          <Button
            size="icon-lg"
            className="rounded-full"
            aria-label={t`Install update`}
            disabled={update.busy}
            onClick={update.install}
          >
            <Download aria-hidden="true" />
          </Button>
        </IconTooltip>
      ) : null}
    </div>
  );
}
